// Lib Core
import {cloneDeep, get, includes, isEmpty, now, omitBy, set} from 'lodash';
import {CancelToken} from 'axios';
import qs from 'qs';
import serviceCaller from '../core/service-caller';

// Redux Actions
import {logout, authenticate} from '../../actions/auth-actions';

// Store
import store from '../../store';

const dispatchLogout = (goHome = true, error) => store.dispatch(logout(goHome, error));
const dispatchRefreshToken = (refreshToken, username) => store.dispatch(authenticate({refreshToken, username}));
const getAuth = () => get(store.getState(), 'auth', {});

const actionsServiceCaller = (actionParams, queryParams = {}, customPath) => {
    const {
        authorization,
        cancelToken,
        dispatchCancel,
        dispatchFetched,
        dispatchError,
        dispatchUnauthenticated,
        noAuthenticationRequired,
        servicesConfig,
    } = actionParams;
    const {accessToken, refreshToken, ttl, user} = getAuth();
    const {
        baseUrl,
        contentType,
        getParams,
        method,
        path,
        payloadType,
        responseType,
        staticToken,
        timeout,
    } = servicesConfig;
    const serviceParams = {
        dispatchCancel,
        dispatchFetched,
        dispatchError,
        dispatchUnauthenticated:
            dispatchUnauthenticated || (!authorization && !noAuthenticationRequired && dispatchLogout),
    };
    const ajaxParams = {
        baseURL: baseUrl,
        cancelToken,
        contentType,
        method,
        params: cloneDeep(getParams) || {},
        paramsSerializer: params => {
            return qs.stringify(params, { arrayFormat: 'repeat' });
          },
        responseType,
        timeout: get(queryParams, 'timeout') || timeout,
        url: customPath ? (String(customPath).startsWith('/') ? customPath : `${path}/${customPath}`) : path,
    };
    const shouldRefreshToken = () => {
        const currentTime = now();
        const minutesToRefresh = 60;

        return (
            !authorization &&
            !actionsServiceCaller.refreshPromise &&
            refreshToken &&
            currentTime > ttl - minutesToRefresh * 60 * 1000 && // refresh if ttl is over minutesToRefresh
            currentTime < ttl // don't refresh if token is expired
        );
    };
    const setAuthHeader = (token) => {
        set(ajaxParams, 'headers.Authorization', `Bearer ${token}`);
    };

    if (!staticToken) {
        if (authorization) {
            if (authorization.refreshToken) {
                set(ajaxParams.params, 'refresh_token', authorization.refreshToken);
                setAuthHeader(accessToken);
            } else {
                set(ajaxParams, 'auth', authorization);
            }
        } else if (accessToken) {
            setAuthHeader(accessToken);
        } else if (!noAuthenticationRequired) {
            dispatchLogout(false, 'Session has expired.');
        }
    }

    if (!isEmpty(queryParams)) {
        if (includes(['POST', 'PUT', 'PATCH'], method) && payloadType !== 'params') {
            set(ajaxParams, 'headers.Content-Type', contentType || 'application/json');
            ajaxParams.data = parseQuery(queryParams);
        } else {
            ajaxParams.params = omitQueryParams({...ajaxParams.params, ...queryParams});
        }
    }

    if (shouldRefreshToken()) {
        actionsServiceCaller.refreshPromise = dispatchRefreshToken(refreshToken, user);
        actionsServiceCaller.refreshPromise.then(
            ({accessToken: newAccessToken, error}) => {
                actionsServiceCaller.refreshPromise = null;
                if (!newAccessToken) {
                    dispatchLogout(false, error || 'Session has timed out.');
                }
            },
            ({error}) => {
                actionsServiceCaller.refreshPromise = null;
                dispatchLogout(!error, error || 'Session refresh failed.');
            }
        );
    }

    return !authorization && actionsServiceCaller.refreshPromise
        ? actionsServiceCaller.refreshPromise.then(({accessToken: newAccessToken}) => {
              if (newAccessToken) {
                  setAuthHeader(newAccessToken);
                  serviceCaller(serviceParams, ajaxParams);
              }
          })
        : serviceCaller(serviceParams, ajaxParams);
};

export const generateCancelToken = () => CancelToken.source();

const omitQueryParams = (query) => omitBy(query, (val, key) => val === undefined || val === null || key === 'timeout');
const parseQuery = (query) => JSON.stringify(omitQueryParams(query));

export default actionsServiceCaller;
